if (!customElements.get("sticky-atc")) {
  customElements.define(
    "sticky-atc",
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          prodTitle: ".m-sticky-addtocart--title",
          mainImage: ".m-sticky-addtocart--image",
          addToCart: ".m-add-to-cart",
          buyNowBtn: ".m-product-dynamic-checkout",
          variantIdSelect: '[name="id"]',
          foxkitBtn: ".foxkit-button",
          select: "select",
        };
        this.hasCustomFields = !!document.querySelector(".m-main-product--info .m-product-custom-field");
      }

      connectedCallback() {
        if (this.listenerController) this.disconnectedCallback();
        this.listenerController = new AbortController();
        this.mainProduct = document.querySelector("product-info[id^='MainProduct']");
        this.container = this.closest(".m-sticky-addtocart");
        this.mainProductForm = document.querySelector(".m-product-form--main");
        this.mainProductInfo = document.querySelector(".m-main-product--info");
        this.mainAddToCart = this.mainProductForm ? this.mainProductForm.querySelector(".m-add-to-cart") : null;
        this.mainDynamicCheckout = this.mainProductForm ? this.mainProductForm.querySelector(this.selectors.buyNowBtn) : null;
        this.disableSelectedVariantDefault = this.mainProduct?.dataset?.disableSelectedVariantDefault === "true" || false;
        if (!this.container) return;
        this.domNodes = queryDomNodes(this.selectors, this.container);

        this.init();
      }

      init() {
        if (!this.mainAddToCart) {
          if (this.container) this.setStickyVisibility(false);
          return;
        }

        this.variantData = this.getVariantData();

        this.setStickyVisibility(false);
        // Recheck the current position after resize or a large scroll jump.
        // An observer alone can miss a move between two off-screen positions.
        this.updateStickyPosition = () => {
          this.visibilityFrame = null;
          this.syncWalletAvailability();
          this.setStickyVisibility(this.shouldShowSticky());
        };
        this.onViewportChange = () => {
          if (this.visibilityFrame == null) {
            this.visibilityFrame = requestAnimationFrame(this.updateStickyPosition);
          }
        };
        window.addEventListener("scroll", this.onViewportChange, { passive: true });
        window.addEventListener("resize", this.onViewportChange);
        window.visualViewport?.addEventListener("resize", this.onViewportChange);
        window.visualViewport?.addEventListener("scroll", this.onViewportChange);
        document.addEventListener("focusin", this.onViewportChange);
        document.addEventListener("focusout", this.onViewportChange);
        this.overlayObserver = new MutationObserver(this.onViewportChange);
        this.overlayObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
        this.overlayObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
        // Native dialogs do not necessarily set the theme's scroll-lock class.
        this.dialogObserver = new MutationObserver(this.onViewportChange);
        this.dialogObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ["open"] });
        if ("IntersectionObserver" in window) {
          this.observer = new IntersectionObserver(this.onViewportChange, { threshold: 0 });
        }
        this.onViewportChange();

        if (this.disableSelectedVariantDefault) {
          this.handleDisableSelectedVariantDefault();
        }

        const { prodTitle, mainImage } = this.domNodes;
        const scrollToProduct = () => {
          if (typeof __scrollToTop === 'function') __scrollToTop();
          else this.mainProduct?.scrollIntoView({ behavior: 'smooth' });
        };
        prodTitle?.addEventListener("click", scrollToProduct, { signal: this.listenerController.signal });
        mainImage?.addEventListener("click", scrollToProduct, { signal: this.listenerController.signal });

        this.handleCustomFields();

        this.setObserveTarget();
        this.syncWithMainProductForm();

        const mobileQuery = window.MinimogTheme?.config?.mediaQueryMobile || "screen and (max-width: 767px)";
        const mql = window.matchMedia(mobileQuery);
        this.stickyMediaQuery = mql;
        this.onStickySizeChange = () => this.setStickyAddToCartHeight();
        mql.addEventListener('change', this.onStickySizeChange);
        this.sizeObserver = new ResizeObserver(entries => {
          const box = entries[0].borderBoxSize;
          this.setStickyAddToCartHeight(box ? (box[0] || box).blockSize : undefined);
        });
        this.sizeObserver.observe(this, { box: 'border-box' });

        this.updateBarSummary();
        this.domNodes.select?.addEventListener("change", (e) => {
          const { target } = e;
          this.updateBarSummary();
          if (this.syncingVariant) return;
          const variantPicker = this.mainProduct?.querySelector('variant-picker');
          const selectedVariantId = this.querySelector(this.selectors.variantIdSelect)?.value;
          if (!selectedVariantId) return;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));
          this.currentVariant ? this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut) : this.toggleAddButton(true, window.MinimogStrings.unavailable);

          const selectedOption = target.options[target.selectedIndex];
          if (!selectedOption) return;
          const selectedOptionIds = (selectedOption.dataset.options || '').split(',').filter(id => id);

          if (variantPicker) {
            selectedOptionIds.forEach((optionId) => {
              const input = variantPicker.querySelector(`[data-option-value-id="${optionId}"]`);
              if (!input) return;
              const { tagName } = input;
              switch (tagName) {
                case "OPTION":
                  const inputParent = input.parentNode; // select tag
                  inputParent.value = input.value;
                  break;
                case "INPUT":
                  input.checked = true;
                  break;
              }
            });
            variantPicker.dispatchEvent(new Event("change"));
          }
        }, { signal: this.listenerController.signal });
      }

      shouldShowSticky() {
        const mobileQuery = window.MinimogTheme?.config?.mediaQueryMobile || "screen and (max-width: 767px)";
        const mobile = window.matchMedia(mobileQuery).matches;
        const enabled = mobile ? this.container.dataset.showOnMobile : this.container.dataset.showOnDesktop;
        if (enabled === "false") return false;
        const locked = [document.documentElement, document.body].some(el =>
          el.classList.contains("prevent-scroll") || el.classList.contains("m:overflow-hidden"));
        if (locked || document.querySelector("dialog[open], .m-cart-drawer--active")) return false;
        const focused = document.activeElement;
        if (focused && !this.contains(focused) && focused.matches('input:not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable="true"]')) return false;
        if (!mobile) return this.mainAddToCart ? this.mainAddToCart.getBoundingClientRect().bottom <= 0 : false;

        const viewport = window.visualViewport;
        const top = viewport?.offsetTop || 0;
        const bottom = top + (viewport?.height || window.innerHeight);
        // A tiny sliver at the viewport edge is not a usable purchase control.
        const usable = [this.mainAddToCart, this.mainDynamicCheckout].some(control => {
          if (!control) return false;
          const rect = control.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0 &&
            getComputedStyle(control).visibility !== "hidden" &&
            Math.min(rect.bottom, bottom) - Math.max(rect.top, top) >= Math.min(44, rect.height);
        });
        return !usable;
      }

      syncWalletAvailability() {
        const wallet = this.domNodes.buyNowBtn;
        if (!wallet) {
          if (this.container.dataset.walletAvailable !== "false") this.container.dataset.walletAvailable = "false";
          return;
        }
        // Native express checkout submits this form directly; it cannot inherit
        // fields merged by the theme's AJAX basket handler or add extra items.
        const hasExtraFields = this.hasCustomFields || this.mainProductForm?.querySelector('[name^="properties["], [name="selling_plan"], input[type="file"]');
        const available = !this.domNodes.addToCart?.disabled && !this.mainAddToCart?.disabled && !hasExtraFields &&
          !document.body.classList.contains("kp-wb-has-extras");
        // Runs every scroll frame just before layout reads: write only on change so an
        // unchanged attribute never invalidates style and forces a synchronous layout.
        if (this.container.dataset.walletAvailable === String(available) && wallet.inert === !available) return;
        this.container.dataset.walletAvailable = String(available);
        wallet.inert = !available;
        wallet.setAttribute("aria-hidden", String(!available));
      }

      getVariantData() {
        const script = this.container.querySelector('[type="application/json"]');
        try {
          this.variantData = this.variantData || (script ? JSON.parse(script.textContent) : []);
        } catch (error) {
          // Malformed variant JSON: keep the bar hidden rather than aborting init().
          this.variantData = [];
          this.setStickyVisibility(false);
        }
        return this.variantData;
      }

      setStickyVisibility(show) {
        if (this.stickyVisible === show) return;
        this.stickyVisible = show;
        this.container.style.setProperty("--m-translate-y", show ? 0 : "100%");
        this.container.inert = !show;
        this.container.setAttribute("aria-hidden", String(!show));
        document.documentElement.classList.toggle("stick-atc-show", show);
      }

      disconnectedCallback() {
        this.listenerController?.abort();
        this.observer?.disconnect();
        this.sizeObserver?.disconnect();
        this.overlayObserver?.disconnect();
        this.dialogObserver?.disconnect();
        this.variantUnsubscribe?.();
        this.stickyMediaQuery?.removeEventListener('change', this.onStickySizeChange);
        window.removeEventListener("scroll", this.onViewportChange);
        window.removeEventListener("resize", this.onViewportChange);
        window.visualViewport?.removeEventListener("resize", this.onViewportChange);
        window.visualViewport?.removeEventListener("scroll", this.onViewportChange);
        document.removeEventListener("focusin", this.onViewportChange);
        document.removeEventListener("focusout", this.onViewportChange);
        if (this.visibilityFrame != null) cancelAnimationFrame(this.visibilityFrame);
        this.visibilityFrame = null;
      }

      setObserveTarget() {
        if (this.observer) {
          this.observer.observe(this.mainAddToCart);
          if (this.mainDynamicCheckout) this.observer.observe(this.mainDynamicCheckout);
          this.observeTarget = this.mainAddToCart;
        }
      }

      setUnavailable() {
        const productForm = this.querySelector('.m-product-form');
        const addButton = productForm?.querySelector('[name="add"]');

        if (addButton) {
          this.toggleAddButton(true, window.MinimogStrings.unavailable);
        }
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('.m-product-form');
        if (!productForm) return;
        const addButton = productForm?.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text && addButtonText) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          if (addButtonText) addButtonText.innerHTML = window.MinimogStrings.addToCart;
        }

        this.syncWalletAvailability();
        if (!modifyClass) return;
      }

      handleDisableSelectedVariantDefault() {
        let urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("variant")) return;

        const pickerFields = this.querySelector('.m-product-option--dropdown-select');
        if (!pickerFields) return;

        pickerFields.value = '';
        this.setUnavailable();
        this.updateBarSummary();
      }

      // The chip's colour dot copies the page's own swatch for the chosen value (colour, or swatch image),
      // so it always matches; values without a swatch (sizes, multi-option titles) show no dot.
      updateBarDot(value) {
        const dot = this.container?.querySelector("[data-kp-mbar-dot]");
        if (!dot) return;
        const picker = this.mainProduct?.querySelector("variant-picker");
        const input = value && picker ? Array.from(picker.querySelectorAll('input[type="radio"]')).find((el) => el.value === value) : null;
        const swatch = input?.id ? picker.querySelector(`label[for="${input.id}"][style]`) : null;
        const style = swatch?.getAttribute("style") || "";
        dot.setAttribute("style", style);
        dot.hidden = !/background/.test(style);
      }

      // Phone bar: the option chip shows the chosen variant and the price beside it follows it.
      updateBarSummary() {
        const select = this.domNodes?.select;
        const option = select?.options?.[select.selectedIndex];
        if (!option) return;
        const chip = this.container?.querySelector("[data-kp-mbar-variant]");
        if (chip) chip.textContent = option.dataset.kpTitle || option.textContent.trim();
        const label = this.container?.querySelector("[data-kp-mbar-label]");
        if (label) {
          label.textContent = option.dataset.kpLabel ? option.dataset.kpLabel + ":" : "";
          label.hidden = !option.dataset.kpLabel;
        }
        this.updateBarDot(option.dataset.kpTitle);
        const now = this.container?.querySelector("[data-kp-mbar-now]");
        if (now && option.dataset.kpPrice) now.textContent = option.dataset.kpPrice;
        const was = this.container?.querySelector("[data-kp-mbar-was]");
        if (was && option.dataset.kpPrice) {
          was.textContent = option.dataset.kpCompare || "";
          was.hidden = !option.dataset.kpCompare;
        }
      }

      setStickyAddToCartHeight(observedHeight) {
        const height = Math.round(observedHeight === undefined ? this.offsetHeight : observedHeight);
        if (height === this.stickyHeight) return;
        this.stickyHeight = height;
        document.documentElement.style.setProperty("--f-sticky-atc-bar-height", height + "px");
        window.MinimogSettings.stickyAddToCartHeight = height;
      }

      syncWithMainProductForm() {
        const variantInput = this.querySelector('[name="id"]');
        if (!variantInput) return;
        this.variantUnsubscribe = MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, (e) => {
          const isMainProduct = e.data.sectionId === this.mainProduct?.dataset?.sectionId;
          if (!isMainProduct) return;
          this.currentVariant = e.data.variant;
          if (this.currentVariant) {
            variantInput.value = e.data.variant.id;
            this.toggleAddButton(!this.currentVariant.available || this.mainAddToCart.disabled, window.MinimogStrings.soldOut)
          } else {
            variantInput.value = '';
            this.toggleAddButton(true, window.MinimogStrings.unavailable)
          }
          // Notify Shopify's native payment component when the main picker changes.
          this.syncingVariant = true;
          variantInput.dispatchEvent(new Event("change", { bubbles: true }));
          this.syncingVariant = false;
        });
      }

      handleCustomFields() {
        if (!this.hasCustomFields) return;

        const { addToCart, buyNowBtn, foxkitBtn } = this.domNodes;

        let hasCustomFieldRequired = false;
        const customFields = document.querySelectorAll(".m-main-product--info .m-product-custom-field");
        let customFieldFirst = customFields[0];
        customFields &&
          customFields.forEach((item) => {
            const field = item.querySelector(".form-field");
            if (field && field.value == "" && field.hasAttribute("required")) {
              hasCustomFieldRequired = true;
            }
          });

        hasCustomFieldRequired &&
          addToCart?.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof __scrollToTop === 'function') __scrollToTop(this.mainProductInfo, () => this.mainAddToCart.click());
            else this.mainProductInfo?.scrollIntoView({ behavior: 'smooth' });
          }, { signal: this.listenerController.signal });
        if (buyNowBtn) {
          buyNowBtn.addEventListener(
            "click",
            (e) => {
              const missing = typeof validateForm === 'function' ? validateForm(this.mainProductInfo) : [];
              if (missing.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                if (typeof __scrollToTop === 'function') __scrollToTop(this.mainProductInfo, () => this.mainDynamicCheckout?.click());
                else this.mainProductInfo?.scrollIntoView({ behavior: 'smooth' });
              }
            },
            { capture: true, signal: this.listenerController.signal }
          );
        }
        if (foxkitBtn) {
          foxkitBtn.addEventListener("click", (e) => {
            e.preventDefault();
            if (typeof __scrollToTop === 'function') __scrollToTop(this.mainProductInfo, () => this.mainAddToCart.click());
            else this.mainProductInfo?.scrollIntoView({ behavior: 'smooth' });
          }, { signal: this.listenerController.signal });
        }
      }
    }
  );
}
